import { useState } from "react";
import { EDIT_PATIENT_PATH } from "Constants/Views";
import { useResolveOpsPrescription } from "CustomHooks/useOperations";
import {
  StatCard,
  Badge,
  SectionTable,
  EmptyRow,
  PaginationFooter,
} from "../components";
import { formatMins, timeAgo, shortDate } from "../format";

// 🔴 Error vs 🟡 Stuck In Flight — the kind of problem this prescription has.
const IssueBadge = ({ type }) =>
  type === "error" ? <Badge tone="red">Error</Badge> : <Badge tone="yellow">Stuck In Flight</Badge>;

// The raw DoseSpot status (Entered / eRxSent / PharmacyVerified / Error / ...),
// colored: red for error states, green when verified, gray for in-flight steps.
const StatusBadge = ({ status }) => (
  <Badge
    tone={
      status === "Error" || status === "EpcsError"
        ? "red"
        : status === "PharmacyVerified"
        ? "green"
        : "gray"
    }
  >
    {status}
  </Badge>
);

// Patient name links (new tab) into the full patient view, where the auditor
// actually resolves it — Orders tab for the Rx, Payments tab for a refund.
const PatientCell = ({ id, name }) =>
  id ? (
    <a
      href={EDIT_PATIENT_PATH.replace(":id", id)}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-chill hover:underline font-medium"
    >
      {name}
    </a>
  ) : (
    <span className="text-gray-500">{name}</span>
  );

// Note-and-confirm dialog. Resolving is a human action (refunded, re-prescribed,
// contacted, ...) — the note is the audit trail of what was done.
const ResolveModal = ({ item, onClose, onSubmit, submitting, isError }) => {
  const [notes, setNotes] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold sapphire mb-1">Resolve prescription</h3>
        <p className="text-sm text-gray-500 mb-4">
          {item.medication} · {item.patientName}
        </p>
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Resolution note (optional)
        </label>
        <textarea
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-chill"
          rows={3}
          placeholder="e.g. Refunded via Payments tab; patient notified."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          autoFocus
        />
        {isError && (
          <p className="mt-2 text-xs text-red-600">Couldn't save — please try again.</p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm rounded-md bg-blue-chill text-white hover:opacity-90 disabled:opacity-50"
            onClick={() => onSubmit(notes)}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Mark resolved"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SubTab = ({ active, label, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 text-sm rounded-md font-medium ${
      active ? "bg-blue-chill text-white" : "text-gray-500 hover:bg-gray-100"
    }`}
  >
    {label}
  </button>
);

/*
 * Prescriptions — a worklist of DoseSpot issues (errors + stuck-in-flight
 * merged) that an auditor works one by one. Each open row links to the patient
 * to act (refund / follow-up), then "Resolve" stamps it (instacuredStatus) so
 * it moves to the Resolved tab. No auto-resend — resolution is a human action.
 */
export const PrescriptionsTab = ({ data, setOpenPage, setResolvedPage }) => {
  const [sub, setSub] = useState("open");
  const [modalItem, setModalItem] = useState(null);
  const resolve = useResolveOpsPrescription();

  const w = data.window;
  const open = data.needsAttention;
  const resolved = data.resolved;

  const submitResolve = (notes) =>
    resolve.mutate(
      { orderId: modalItem.orderId, notes },
      { onSuccess: () => setModalItem(null) }
    );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Prescriptions" value={w.total} sub={`${w.inFlight} in flight`} />
        <StatCard label="Verified" value={w.verified} sub="pharmacy verified" />
        <StatCard
          label="Needs Attention"
          value={open.total}
          sub={`errors + stuck in flight (unsent > ${data.stuckThresholdMins}m)`}
          alert={open.total > 0}
        />
        <StatCard label="Resolved" value={resolved.total} sub="handled in this range" />
      </div>

      <div className="rounded-lg bg-white shadow-lg px-6 py-4 flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium text-gray-500 mr-2">Status breakdown:</span>
        {Object.entries(w.byStatus).length === 0 && (
          <span className="text-sm text-gray-400">no prescriptions</span>
        )}
        {Object.entries(w.byStatus).map(([status, count]) => (
          <Badge
            key={status}
            tone={
              status === "Error" || status === "EpcsError"
                ? "red"
                : status === "PharmacyVerified"
                ? "green"
                : "gray"
            }
          >
            {status}: {count}
          </Badge>
        ))}
      </div>

      <div className="flex gap-2">
        <SubTab
          active={sub === "open"}
          label={`Needs Attention (${open.total})`}
          onClick={() => setSub("open")}
        />
        <SubTab
          active={sub === "resolved"}
          label={`Resolved (${resolved.total})`}
          onClick={() => setSub("resolved")}
        />
      </div>

      {sub === "open" ? (
        <SectionTable
          title="Needs Attention"
          headers={["Issue", "Medication", "Patient", "Doctor", "DoseSpot", "Detail", "Since", ""]}
          footer={
            <PaginationFooter
              page={open.page}
              totalPages={Math.max(1, Math.ceil(open.total / open.perPage))}
              total={open.total}
              onPage={setOpenPage}
            />
          }
        >
          {open.rows.length === 0 && (
            <EmptyRow colSpan={8} text="Nothing needs attention — all recent prescriptions are healthy." />
          )}
          {open.rows.map((o) => (
            <tr key={o.orderId}>
              <td className="px-6 py-3"><IssueBadge type={o.type} /></td>
              <td className="px-6 py-3 text-sm font-medium sapphire">{o.medication}</td>
              <td className="px-6 py-3 text-sm">
                <PatientCell id={o.patientId} name={o.patientName} />
              </td>
              <td className="px-6 py-3 text-sm text-gray-500">{o.doctorName}</td>
              <td className="px-6 py-3"><StatusBadge status={o.status} /></td>
              <td className="px-6 py-3 text-xs text-gray-500 max-w-md">{o.detail || "—"}</td>
              <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                {shortDate(o.createdAt)}
                <div className="text-xs text-gray-400">{formatMins(o.ageMins)} old</div>
              </td>
              <td className="px-6 py-3 text-right">
                <button
                  className="px-3 py-1.5 text-xs rounded-md bg-blue-chill text-white hover:opacity-90"
                  onClick={() => setModalItem(o)}
                >
                  Resolve
                </button>
              </td>
            </tr>
          ))}
        </SectionTable>
      ) : (
        <SectionTable
          title="Resolved"
          headers={["Issue", "Medication", "Patient", "Doctor", "DoseSpot", "Notes", "Resolved by", "When"]}
          footer={
            <PaginationFooter
              page={resolved.page}
              totalPages={Math.max(1, Math.ceil(resolved.total / resolved.perPage))}
              total={resolved.total}
              onPage={setResolvedPage}
            />
          }
        >
          {resolved.rows.length === 0 && (
            <EmptyRow colSpan={8} text="No resolved prescriptions in this range." />
          )}
          {resolved.rows.map((o) => (
            <tr key={o.orderId}>
              <td className="px-6 py-3"><IssueBadge type={o.type} /></td>
              <td className="px-6 py-3 text-sm font-medium sapphire">{o.medication}</td>
              <td className="px-6 py-3 text-sm">
                <PatientCell id={o.patientId} name={o.patientName} />
              </td>
              <td className="px-6 py-3 text-sm text-gray-500">{o.doctorName}</td>
              <td className="px-6 py-3"><StatusBadge status={o.status} /></td>
              <td className="px-6 py-3 text-xs text-gray-500 max-w-md">{o.notes || "—"}</td>
              <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                {o.resolvedBy || "—"}
              </td>
              <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                {o.resolvedAt ? timeAgo(o.resolvedAt) : "—"}
              </td>
            </tr>
          ))}
        </SectionTable>
      )}

      {modalItem && (
        <ResolveModal
          item={modalItem}
          submitting={resolve.isLoading}
          isError={resolve.isError}
          onClose={() => setModalItem(null)}
          onSubmit={submitResolve}
        />
      )}
    </>
  );
};
